/**
 * Audit Entry Card Component
 * 
 * Displays a single audit entry with expandable details
 */

import { useState } from 'react';
import type { AuditEntry, AuditChange } from '@/types/audit';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

interface AuditEntryCardProps {
  entry: AuditEntry;
}

const actionIcons: Record<string, string> = {
  created: '✨',
  saved_draft: '💾',
  modified: '✏️',
  submitted: '📤',
  approved: '✅',
  rejected: '❌',
  resubmitted: '🔄',
  deleted: '🗑️',
  locked: '🔒',
  unlocked: '🔓',
  comment_added: '💬',
};

const actionLabels: Record<string, string> = {
  created: 'Created',
  saved_draft: 'Saved Draft',
  modified: 'Modified',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  resubmitted: 'Resubmitted',
  deleted: 'Deleted',
  locked: 'Locked',
  unlocked: 'Unlocked',
  comment_added: 'Comment Added',
};

const actionColors: Record<string, string> = {
  created: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  saved_draft: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  modified: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  submitted: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  resubmitted: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  deleted: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  locked: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  unlocked: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  comment_added: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
};

function ChangeDetail({ change }: { change: AuditChange }) {
  const formatValue = (value: any) => {
    if (value === null || value === undefined) return 'None';
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const getFieldLabel = (field: string) => {
    const labels: Record<string, string> = {
      entry_added: 'Entry Added',
      entry_removed: 'Entry Removed',
      hours_mon: 'Monday Hours',
      hours_tue: 'Tuesday Hours',
      hours_wed: 'Wednesday Hours',
      hours_thu: 'Thursday Hours',
      hours_fri: 'Friday Hours',
      hours_sat: 'Saturday Hours',
      hours_sun: 'Sunday Hours',
      description: 'Description',
      project: 'Project',
      task: 'Task',
    };
    return labels[field] || field;
  };

  return (
    <div className="text-xs space-y-1 p-2 bg-muted/50 rounded">
      <div className="font-medium text-muted-foreground">{getFieldLabel(change.field)}</div>
      {change.field === 'entry_added' ? (
        <div className="text-green-600 dark:text-green-400">
          + {formatValue(change.newValue)}
        </div>
      ) : change.field === 'entry_removed' ? (
        <div className="text-red-600 dark:text-red-400">
          - {formatValue(change.oldValue)}
        </div>
      ) : (
        <div className="space-y-1">
          <div className="text-red-600 dark:text-red-400">
            - {formatValue(change.oldValue)}
          </div>
          <div className="text-green-600 dark:text-green-400">
            + {formatValue(change.newValue)}
          </div>
        </div>
      )}
    </div>
  );
}

export function AuditEntryCard({ entry }: AuditEntryCardProps) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = (entry.changes && entry.changes.length > 0) || entry.comment;
  
  // Special styling for rejection entries
  const isRejection = entry.actionType === 'rejected';
  const cardClassName = isRejection 
    ? 'mb-3 border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20' 
    : 'mb-3';

  return (
    <Card className={cardClassName}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="text-2xl flex-shrink-0 mt-1">
            {actionIcons[entry.actionType] || '📝'}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={actionColors[entry.actionType] || 'bg-gray-100'}>
                    {actionLabels[entry.actionType] || entry.actionType}
                  </Badge>
                  <span className="text-sm font-medium">{entry.actorName}</span>
                  {entry.actorRole && (
                    <span className="text-xs text-muted-foreground">
                      ({entry.actorRole})
                    </span>
                  )}
                </div>
                
                {/* Status change */}
                {entry.previousStatus && entry.newStatus && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Status: <span className="font-medium">{entry.previousStatus}</span>
                    {' → '}
                    <span className="font-medium">{entry.newStatus}</span>
                  </div>
                )}
              </div>

              {/* Timestamp */}
              <div className="text-xs text-muted-foreground text-right flex-shrink-0">
                <div>{formatDistanceToNow(entry.timestamp, { addSuffix: true })}</div>
                <div className="text-[10px]">
                  {format(entry.timestamp, 'MMM d, yyyy h:mm a')}
                </div>
              </div>
            </div>

            {/* Rejection Reason - Prominently displayed for rejections */}
            {isRejection && entry.comment && (
              <div className="text-sm bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-3 rounded mb-2">
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-red-900 dark:text-red-100">Rejection Reason:</span>
                </div>
                <div className="mt-1 text-red-800 dark:text-red-200">{entry.comment}</div>
              </div>
            )}

            {/* Comment (for non-rejection entries) */}
            {!isRejection && entry.comment && (
              <div className="text-sm bg-muted/50 p-2 rounded mb-2">
                <span className="font-medium">Comment: </span>
                {entry.comment}
              </div>
            )}

            {/* Changes summary */}
            {entry.changes && entry.changes.length > 0 && !expanded && (
              <div className="text-xs text-muted-foreground">
                {entry.changes.length} change{entry.changes.length !== 1 ? 's' : ''}
              </div>
            )}

            {/* Expanded details */}
            {expanded && entry.changes && entry.changes.length > 0 && (
              <div className="mt-2 space-y-2">
                {entry.changes.map((change, index) => (
                  <ChangeDetail key={index} change={change} />
                ))}
              </div>
            )}

            {/* Expand/collapse button */}
            {hasDetails && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="mt-2 h-7 text-xs"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="h-3 w-3 mr-1" />
                    Hide Details
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3 mr-1" />
                    View Details
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
